<script setup lang="ts">
const userStore = useUserStore()
const modalStore = useModalStore()
</script>

<template>
  <div class="px-4 py-3 mx-auto max-w-7xl sm:px-6 md:px-8">
    <div class="relative pt-16 pb-16 overflow-hidden bg-white rounded shadow">
      <div class="mt-0">
        <div class="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-18">
          <div class="px-4 max-w-xl mx-auto sm:px-6 lg:py-0 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-2">
            <div>
              <div>
                <span class="h-12 w-12 rounded-md flex items-center justify-center bg-tomate-400">
                  <UIcon name="i-mdi-lightbulb-on" class="h-6 w-6 text-white" aria-hidden="true" />
                </span>
              </div>
              <div class="mt-6">
                <h2 class="text-3xl font-extrabold tracking-tight text-poivre-800 font-title">
                  Trouvez les vrais rabais
                </h2>
                <p class="mt-4 text-lg text-poivre-500">
                  Glouton évalue et donne un score aux offres des circulaires, pour vous aider à trouver celles qui valent vraiment la peine.
                </p>
                <div class="mt-4 text-lg text-poivre-500">
                  <p>Consultez les spéciaux avec les évaluations de Glouton affichées directement dans les circulaires interactives, ou bien choisissez le mode d'affichage en grille pour voir les meilleures aubaines en premier.</p>
                </div>
                <div class="mt-4 text-lg text-poivre-500">
                  <p>En cliquant sur le menu<UIcon name="i-mdi-dots-vertical" class="inline" />en haut à droite d'une circulaire, vous accédez à des outils pour faciliter la préparation de votre épicerie. Faites des recherches parmi les offres et préparez votre liste d'épicerie en un clin d'oeil.</p>
                </div>
                <div class="mt-4 text-lg text-poivre-500">
                  <p>
                    Tout ça est disponible <b>gratuitement</b>. Il suffit de <ULink :to="$localePath({ name: 'RegisterPage' })" class="underline text-tomate">
                      créer un compte
                    </ULink> pour voir nos analyses de prix dans les circulaires.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-12 sm:mt-16 lg:mt-0 lg:col-start-1">
            <div class="pr-4 -ml-48 sm:pr-6 md:-ml-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
              <img class="w-full shadow-xl lg:absolute lg:right-0 lg:h-full lg:w-auto lg:max-w-none" src="https://glouton.b-cdn.net/site/images/mockups/new_flyers3.jpg" alt="Interface des circulaires">
            </div>
          </div>
        </div>
      </div>
      <div class="relative mt-24">
        <div class="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
          <div class="max-w-xl px-4 mx-auto sm:px-6 lg:py-8 lg:max-w-none lg:mx-0 lg:px-0">
            <div>
              <div>
                <span class="flex items-center justify-center w-12 h-12 rounded-md bg-tomate-400">
                  <UIcon name="i-mdi-tag-multiple" class="w-6 h-6 text-white" aria-hidden="true" />
                </span>
              </div>
              <div class="mt-6">
                <h2 class="text-3xl font-extrabold tracking-tight text-poivre-800 font-title">
                  L'analyse des prix de Glouton
                </h2>
                <p class="mt-4 text-lg text-poivre-500">
                  Les produits des circulaires sont analysés et obtiennent un score sur 10, qui est calculé selon les paramètres suivants:
                </p>
                <ul class="text-lg text-poivre-500 list-decimal list-inside ml-4 mt-3">
                  <li><strong>la saisonnalité:</strong> est-ce que l'article est de saison et disponible localement?</li>
                  <li><strong>la rareté:</strong> à quelle fréquence une telle promotion se présente t-elle?</li>
                  <li><strong>la qualité:</strong> à quel point la promotion est une aubaine comparée au prix régulier?</li>
                </ul>
                <p class="mt-4 text-lg text-poivre-500">
                  Plus le score est élevé, plus l'aubaine est intéressante. Par exemple : un article avec un score de 8 indique une aubaine très intéressante, tandis qu'un score de 10 indique une aubaine exceptionnellement avantageuse.
                </p>
                <p class="mt-4 text-lg text-poivre-500">
                  Le score est affiché sur les promotions analysées avec ces symboles:
                </p>
                <div class="flex items-center mt-2">
                  <div class="opacity-90 rounded-full text-lg cursor-pointer h-10 w-10 text-center grid place-items-center" style="background-color: rgb(255, 0, 0); color: white;">
                    <div class="font-medium inline-block align-middle">
                      1
                    </div>
                  </div>
                  <div class="px-2">
                    &rarr;
                  </div>
                  <div class="opacity-90 rounded-full text-lg cursor-pointer h-10 w-10 text-center grid place-items-center" style="background-color: rgb(76, 183, 2); color: white;">
                    <div class="font-medium inline-block align-middle">
                      10
                    </div>
                  </div>
                </div>
                <p class="mt-4 text-lg text-poivre-500">
                  Ce score est utilisé pour calculer l'indice d'économie affiché sur les fiches de recettes. Plus cet indice est élevé, plus la recette est avantageuse en combinant les différents rabais.
                </p>
                <p class="mt-4 text-lg text-poivre-500">
                  L'indice est calculé selon les commerces favoris de votre profil et variera donc selon les combinaisons.
                </p>
                <h3 class="mt-4 text-xl font-medium text-poivre-800 font-title">
                  Pourquoi certains articles n'ont pas de score?
                </h3>
                <p class="mt-2 text-lg text-poivre-500">
                  Nous mettons l'accent sur les promotions utiles en cuisine, ce qui signifie que certains produits en formats individuels ou pré-cuisinés peuvent ne pas afficher de score. De même, les nouveaux produits avec peu de données historiques ou des prix moins intéressants peuvent également n'avoir aucun score affiché.
                </p>
              </div>
            </div>
          </div>
          <div class="mt-12 sm:mt-16 lg:mt-0">
            <div class="pl-4 -mr-48 sm:pl-6 md:-mr-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
              <img class="w-full shadow-xl lg:absolute lg:left-0 lg:h-full lg:w-auto lg:max-w-none" src="https://glouton.b-cdn.net/site/images/mockups/new_flyers4.jpg" alt="Analyse des prix">
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-10 bg-white rounded shadow">
      <div class="px-4 py-12 mx-auto text-center max-w-7xl sm:px-6 lg:py-16 lg:px-8">
        <h2 class="mb-2 text-3xl font-extrabold tracking-tight text-poivre-800 sm:text-4xl font-title">
          <span class="block">Prêt à économiser?</span>
        </h2>
        <p class="text-sm text-poivre-600">
          Un compte gratuit permet de profiter de toutes les options de cet outil, sans limitation.
        </p>
        <div v-if="!userStore.user" class="flex justify-center mt-5 space-x-3">
          <UButton type="button" icon="i-mdi-login-variant" label="Connexion" @click="modalStore.openLoginModal" />
          <UButton as="a" :to="$localePath({ name: 'RegisterPage' })" label="Créer un compte" />
        </div>
      </div>
    </div>
  </div>
</template>
